import {
  type LucideIcon,
  LayoutDashboard,
  Building2,
  BookOpen,
  GraduationCap,
  Users,
  Briefcase,
  PhoneCall,
  ListChecks,
  KanbanSquare,
  CalendarCheck,
  CheckCircle2,
  Clock3,
  CreditCard,
  Landmark,
  Receipt,
  Wallet,
  CalendarDays,
  Bell,
  Star,
} from 'lucide-react';

export type NavTone =
  | 'money'
  | 'people'
  | 'ops'
  | 'pipeline'
  | 'alert'
  | 'golden'
  | 'cherry'
  | 'navy'
  | 'cherryBrown';

export const NAV_TONE_ICON: Record<NavTone, string> = {
  money: 'text-emerald-500 dark:text-emerald-400',
  people: 'text-blue-600 dark:text-blue-400',
  ops: 'text-sky-500 dark:text-sky-400',
  pipeline: 'text-green-500 dark:text-green-400',
  alert: 'text-rose-500 dark:text-rose-400',
  golden: 'text-amber-500 dark:text-amber-300',
  cherry: 'text-red-600 dark:text-red-400',
  navy: 'text-blue-900 dark:text-blue-300',
  cherryBrown: 'text-amber-800 dark:text-orange-300',
};

export type WebRole = 'admin' | 'manager' | 'trainer';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  // Optional human-readable group for the Command palette.
  group?: string;
  // Optional semantic tone for icon color (matches the dashboard KPI palette).
  tone?: NavTone;
  // Roles permitted to see this item. Omitted = visible to all web roles.
  // This is an ALLOWLIST: with three roles, a per-item list is the only way to
  // express e.g. "managers but not trainers" (Branches, Trainers). Mirror these
  // against the route guards in routes/index.tsx.
  roles?: WebRole[];
}

export const NAV: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
  { to: '/admin/branches', label: 'Branches', icon: Building2, group: 'Academy', tone: 'cherryBrown', roles: ['admin', 'manager'] },
  { to: '/admin/batches', label: 'Batches', icon: BookOpen, group: 'Academy', tone: 'cherry' },
  { to: '/admin/students', label: 'Students', icon: GraduationCap, group: 'People', tone: 'navy' },
  { to: '/admin/trainers', label: 'Trainers', icon: Users, group: 'People', tone: 'people', roles: ['admin', 'manager'] },
  { to: '/admin/managers', label: 'Managers', icon: Briefcase, group: 'People', tone: 'people', roles: ['admin'] },
  { to: '/admin/leads', label: 'Leads', icon: PhoneCall, group: 'People', tone: 'pipeline' },
  { to: '/admin/leads/work-queue', label: 'Follow-ups', icon: ListChecks, group: 'People', tone: 'pipeline' },
  { to: '/admin/leads/board', label: 'Pipeline', icon: KanbanSquare, group: 'People', tone: 'pipeline' },
  { to: '/admin/todays-classes', label: "Today's classes", icon: Clock3, group: 'Operations', tone: 'ops' },
  { to: '/admin/attendance', label: 'Attendance', icon: CalendarCheck, group: 'Operations', tone: 'ops' },
  // Scoped per role server-side: academy / branches / batches.
  { to: '/admin/recently-conducted-classes', label: 'Conducted classes', icon: CheckCircle2, group: 'Operations', tone: 'ops' },
  { to: '/admin/fees', label: 'Fees', icon: CreditCard, group: 'Operations', tone: 'money' },
  { to: '/admin/other-fees', label: 'Other fees', icon: GraduationCap, group: 'Operations', tone: 'money' },
  { to: '/admin/salary', label: 'Salary', icon: Wallet, group: 'Operations', tone: 'money' },
  { to: '/admin/calendar', label: 'Calendar', icon: CalendarDays, group: 'Operations', tone: 'ops' },
  // Managers and trainers land on the invoice view; the payout tab is admin-only.
  { to: '/admin/settlements', label: 'Settlements', icon: Landmark, group: 'Operations', tone: 'money', roles: ['admin', 'manager', 'trainer'] },
  { to: '/admin/payments', label: 'Payments', icon: Receipt, group: 'Operations', tone: 'money', roles: ['admin'] },
  { to: '/admin/notifications', label: 'Notifications', icon: Bell, group: 'Account', tone: 'alert' },
  { to: '/admin/membership', label: 'Membership', icon: Star, group: 'Account', tone: 'golden', roles: ['admin'] },
];

/** Nav items visible to the given profile role. An item with no `roles` list is
 *  shown to everyone; otherwise the role must be in the list. */
export function navForRole(role: string | undefined): NavItem[] {
  return NAV.filter((i) => !i.roles || (role !== undefined && (i.roles as string[]).includes(role)));
}

export interface QuickAction {
  label: string;
  to: string;
  roles?: WebRole[];
}

export const NAV_QUICK_ACTIONS: QuickAction[] = [
  { label: 'Add a branch', to: '/admin/branches/new', roles: ['admin'] },
  { label: 'Add a student', to: '/admin/students/new' },
  { label: 'Add a trainer', to: '/admin/trainers/new', roles: ['admin', 'manager'] },
  // Trainers have no batch-create endpoint (see routes/api.trainers.php).
  { label: 'Add a batch', to: '/admin/batches/new', roles: ['admin', 'manager'] },
];

/** Quick actions available to the given role (same allowlist semantics as nav). */
export function quickActionsForRole(role: string | undefined): QuickAction[] {
  return NAV_QUICK_ACTIONS.filter(
    (q) => !q.roles || (role !== undefined && (q.roles as string[]).includes(role)),
  );
}
